import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  BodyText,
  Button,
  Div,
  Dropdown,
  DropdownMultiselect,
  Flex,
  GridBag,
  Gutter,
  GutterBag,
  Headline,
  Input,
  Link,
  ModalDialog,
  OptionListOption,
  Title,
} from '@gaiads/telia-react-component-library';
import { SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';

import { selectAllLocales } from '../../store/locales';
import { addOneSalesChannel, removeOneSalesChannel, updateOneSalesChannel } from '../../store/salesChannels';
import ProductInformation from './ProductInformation/ProductInformation';
import { RootState } from '../../store';
import { getInitialSalesChannel } from '../../utils/products';
import { setNotification } from '../../store/ui';
import { productServiceAdminSalesChannelsClient } from '../../api-client/product-service-client';
import { selectAllCountries } from '../../store/adminCountries';

interface SalesChannelFormParamTypes {
  salesChannelUuid?: string;
}

export default function SalesChannelForm() {
  const salesChannelUuid = useParams<SalesChannelFormParamTypes>().salesChannelUuid as SalesChannel['uuid'];

  const [salesChannel, setSalesChannel] = useState<SalesChannel>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const countries = useSelector((state: RootState) => selectAllCountries(state));
  const salesChannelCountry = useMemo(() => countries?.find(country => country.id === salesChannel?.country_id), [countries, salesChannel]);
  const locales = useSelector(selectAllLocales);
  const { t } = useTranslation('saleschannelform');
  const history = useHistory();
  const dispatch = useDispatch();

  const settings = salesChannel?.settings;
  const orderedSettings: { [key: string]: boolean } = {
    show_product_images: settings?.show_product_images || false,
    show_product_description: settings?.show_product_description || false,
    show_customer_reviews: settings?.show_customer_reviews || false,
    show_product_key_selling_points: settings?.show_product_key_selling_points || false,
    show_product_videos: settings?.show_product_videos || false,
    show_technical_specification: settings?.show_technical_specification || false,
  };

  useEffect(() => {
    const fetchSalesChannel = async () => {
      if (salesChannelUuid) {
        try {
          const response = await productServiceAdminSalesChannelsClient.getSalesChannel1(salesChannelUuid);
          setSalesChannel(response.data.sales_channel);
        } catch {
          dispatch(
            setNotification({
              text: t('errors.fetch'),
              show: true,
              type: 'warning',
            }),
          );
        }
      }
    };
    if (salesChannelUuid) {
      fetchSalesChannel();
    } else {
      const initialSalesChannel = getInitialSalesChannel();
      setSalesChannel(initialSalesChannel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!salesChannel) return <></>;

  const validate = (salesChannel: SalesChannel) => {
    let valid = true;
    const newErrors: Record<string, string> = {};
    if (salesChannel.name === '') {
      newErrors['name'] = t('errors.name_blank');
      valid = false;
    }
    if (!salesChannel.country_id) {
      newErrors['countryId'] = t('errors.countryId_empty');
      valid = false;
    }
    if (!salesChannel.locale_ids.length) {
      newErrors['localeIds'] = t('errors.localeIds_empty');
      valid = false;
    }
    if (!valid) {
      setErrors(newErrors);
    }
    return valid;
  };

  const createSalesChannel = async (salesChannel: SalesChannel) => {
    if (validate(salesChannel)) {
      let response;
      try {
        response = await productServiceAdminSalesChannelsClient.postSalesChannel(salesChannel);
        const createdSalesChannel = response.data;
        dispatch(addOneSalesChannel(createdSalesChannel));
        history.push('/saleschannels');
      } catch {
        dispatch(
          setNotification({
            text: t('errors.create_sales_channel'),
            show: true,
            type: 'warning',
          }),
        );
      }
    } else {
      dispatch(
        setNotification({
          text: t('errors.create_sales_channel'),
          show: true,
          type: 'warning',
        }),
      );
    }
  };

  const updateSalesChannel = async (salesChannel: SalesChannel) => {
    if (validate(salesChannel)) {
      try {
        const response = await productServiceAdminSalesChannelsClient.putSalesChannel(salesChannel.id, salesChannel);
        const updatedSalesChannel = response.data;
        dispatch(updateOneSalesChannel(updatedSalesChannel));
        history.push('/saleschannels');
      } catch {
        dispatch(
          setNotification({
            text: t('errors.update_sales_channel'),
            show: true,
            type: 'warning',
          }),
        );
      }
    } else {
      dispatch(
        setNotification({
          text: t('errors.update_sales_channel'),
          show: true,
          type: 'warning',
        }),
      );
    }
  };

  const removeSalesChannel = async (id: SalesChannel['id']) => {
    try {
      await productServiceAdminSalesChannelsClient.deleteSalesChannel(id);
      dispatch(removeOneSalesChannel(id));
      history.push('/saleschannels');
    } catch {
      dispatch(
        setNotification({
          text: t('errors.remove_sales_channel'),
          show: true,
          type: 'warning',
        }),
      );
    }
  };

  const removeError = (field: string) => {
    const updatedErrors = { ...errors };
    delete updatedErrors[field];
    setErrors(updatedErrors);
  };

  const handleNameChange = (name: SalesChannel['name']) => {
    setSalesChannel({ ...salesChannel, name });

    removeError('name');
  };
  const handleCountryChange = (option: OptionListOption) => {
    const updatedSalesChannel: SalesChannel = {
      ...salesChannel,
      country_id: Number(option.value),
    };
    setSalesChannel(updatedSalesChannel);

    removeError('countryId');
  };

  const handleLanguageChange = (option: OptionListOption) => {
    const localeId = Number(option.value);
    let localeIds = [...salesChannel.locale_ids];
    if (!option.checked && !localeIds.includes(localeId)) {
      localeIds.push(localeId);
    } else {
      localeIds = localeIds.filter(id => id !== localeId);
    }
    const updatedSalesChannel: SalesChannel = {
      ...salesChannel,
      locale_ids: localeIds,
    };
    setSalesChannel(updatedSalesChannel);

    removeError('localeIds');
  };
  const handleLanguageClear = () => {
    const updatedSalesChannel = {
      ...salesChannel,
      locale_ids: [],
    };
    setSalesChannel(updatedSalesChannel);
  };

  const handleCreate = async () => {
    createSalesChannel(salesChannel);
  };
  const handleUpdate = async () => {
    updateSalesChannel(salesChannel);
  };
  const handleDelete = async () => {
    removeSalesChannel(salesChannel.id);
  };
  let title, buttons;
  if (salesChannelUuid) {
    title = t('titles.update_sales_channel');
    buttons = (
      <Flex alignBaseline>
        <Button type="submit" variant="primary" size="lg" margin={{ right: 'sm' }} onClick={handleUpdate}>
          {t('buttons.update')}
        </Button>
        <Button variant="primary" size="lg" onClick={() => setIsDeleteConfirmationOpen(true)}>
          {t('buttons.delete_sales_channel')}
        </Button>
        <Gutter />
        <Link to={'/saleschannels'}>{t('buttons.cancel')}</Link>
      </Flex>
    );
  } else {
    title = t('titles.add_sales_channel');
    buttons = (
      <Flex alignBaseline>
        <Button type="submit" variant="primary" size="lg" margin={{ right: 'sm' }} onClick={handleCreate}>
          {t('buttons.create')}
        </Button>
        <Gutter />
        <Link to={'/saleschannels'}>{t('buttons.cancel')}</Link>
      </Flex>
    );
  }
  return (
    <Div margin={{ vertical: 'lg', horizontal: 'xxlg' }}>
      <Headline>{title}</Headline>
      <Gutter />

      <GridBag>
        <GridBag.Item md={6}>
          <Input
            name="name"
            aria-label={t('labels.name')}
            label={t('labels.name')}
            value={salesChannel.name}
            onChange={event => handleNameChange(event.target.value)}
            errorText={errors.name}
            required
          />
          <Gutter />
          <Title>{t('titles.locale')}</Title>
          <BodyText>{t('texts.choose_locale')}</BodyText>
          <Gutter />

          <Dropdown
            label={t('labels.country')}
            options={
              countries?.map(country => ({
                value: String(country.id),
                label: country.name,
              })) || []
            }
            selectedOption={
              salesChannelCountry && {
                value: String(salesChannelCountry.id),
                label: salesChannelCountry.name,
              }
            }
            onChange={(_event, option) => handleCountryChange(option)}
            errorText={errors.countryId}
            required
          />
          <Gutter size="md" />
          <DropdownMultiselect
            label={t('labels.languages')}
            options={locales.map(locale => ({
              value: String(locale.id),
              label: locale.name,
              checked: salesChannel.locale_ids.includes(locale.id),
            }))}
            onChange={(_event, option) => handleLanguageChange(option)}
            onClear={() => handleLanguageClear()}
            errorText={errors.localeIds}
            required
          />
          <Gutter />

          <Title>{t('titles.product_information')}</Title>
          <BodyText>{t('texts.product_information')}</BodyText>
          <Gutter />
          <GutterBag
            pileVerticallyTo={'xlg'}
            contents={Object.keys(orderedSettings).map(setting => (
              <ProductInformation
                key={`product-information-${setting}`}
                setting={setting}
                value={orderedSettings[setting]}
                salesChannel={salesChannel}
                setSalesChannel={setSalesChannel}
              />
            ))}
          />
        </GridBag.Item>
      </GridBag>
      <Gutter size="lg" />
      {buttons}
      <ModalDialog
        title={t('delete_sales_channel_confirmation_dialog.title')}
        closeButtonText={t('delete_sales_channel_confirmation_dialog.buttons.cancel')}
        acceptButtonText={t('delete_sales_channel_confirmation_dialog.buttons.delete')}
        isOpen={isDeleteConfirmationOpen}
        onCloseClick={() => setIsDeleteConfirmationOpen(false)}
        onAcceptClick={handleDelete}
      >
        <BodyText>{t('delete_sales_channel_confirmation_dialog.content')}</BodyText>
      </ModalDialog>
    </Div>
  );
}
